import { put, select, takeLatest } from 'redux-saga/effects';
import { createPanelApiSaga } from '@/features/auth/store/panel-api-saga';
import { fetchReferralCodes } from '@/features/referral-codes/store/referral-codes.slice';
import {
  fetchSalesInquiries,
  fetchSalesInquiry,
} from '@/features/sales/store/sales-inquiries.slice';
import { toSalesQuery } from '@/features/sales/model/sales-query';
import type { RootState } from '@/shared/store/store';
import * as organizationsApi from '../api/panel-organizations.api';
import * as creditsApi from '../api/panel-credits.api';
import { toOrganizationQuery } from '../model/organization-query';
import { refetchesAfterFailure } from '../model/write-outcome';
import {
  createContract,
  fetchCreditLedger,
  fetchCreditOverview,
  grantCredits,
  reverseCredits,
  terminateContract,
} from './credits.slice';
import { disableEventSurvey, fetchOrganizationPostEvent } from './organization-post-event.slice';
import { fetchOrganizationReports } from './organization-reports.slice';
import {
  checkOwnerEmail,
  fetchOrganization,
  fetchOrganizations,
  provisionOrganization,
  removeOrganizationCover,
  replaceOwnerInvitation,
  resendOwnerInvitation,
} from './organizations.slice';

const apiSaga = createPanelApiSaga();

/** Kurum listesini ekranın o anki süzgeciyle tazeler. */
function* refreshOrganizationList() {
  const filters = (yield select(
    (state: RootState) => state.panelOrgs.filters,
  )) as RootState['panelOrgs']['filters'];
  yield put(fetchOrganizations.request(toOrganizationQuery(filters)));
}

/** Satış adayı listesi YALNIZ yüklenmişse tazelenir (kurum adaydan açıldıysa). */
function* refreshSalesListIfLoaded() {
  const sales = (yield select((state: RootState) => state.panelSales)) as RootState['panelSales'];
  if (sales.list.phase === 'idle') return;
  yield put(fetchSalesInquiries.request(toSalesQuery(sales.filters)));
}

/** Kredi yazmasından sonra: özet + defter (aynı sayfa) + kurum listesi. */
function* refreshCredits(organizationId: string, withLedger: boolean) {
  yield put(fetchCreditOverview.request({ organizationId }));
  if (withLedger) {
    const ledger = (yield select(
      (state: RootState) => state.panelCredits.ledger,
    )) as RootState['panelCredits']['ledger'];
    // Yeni satır EN ÜSTE düşer: vurgunun görünmesi için ilk sayfaya dönülür.
    yield put(
      fetchCreditLedger.request({
        organizationId,
        page: 1,
        pageSize: ledger.pageSize || LEDGER_PAGE_SIZE,
      }),
    );
  }
  yield* refreshOrganizationList();
}

/**
 * Hediyeli yüklemenin bayatlattığı iki yüzey (`INVALIDATION_MAP`
 * `panelCredits/grant`): referans kodu listesindeki kullanım sayısı ve açık
 * satış adayı detayındaki kod durumu. İkisi de YALNIZ yüklenmişse tazelenir:
 * kapalı ekran için istek atılmaz (aday detayının her okuması denetim izidir).
 */
function* refreshGiftSurfaces() {
  const referral = (yield select(
    (state: RootState) => state.panelReferral.list,
  )) as RootState['panelReferral']['list'];
  if (referral.loaded || referral.phase !== 'idle') yield put(fetchReferralCodes.request());
  const detailId = (yield select((state: RootState) => state.panelSales.detail.id)) as
    | string
    | null;
  if (detailId) yield put(fetchSalesInquiry.request({ id: detailId }));
}

export const LEDGER_PAGE_SIZE = 20;

/**
 * KURUM VE KREDİ SAGALARI.
 *
 * OKUMALAR `takeLatest`: kurumdan kuruma hızlı geçişte eski kurumun cevabı
 * yenisinin yerine yazılamaz.
 *
 * YAZMALAR da `takeLatest` ama tekillik DÜĞME KİLİDİNDEN gelir: diyalog
 * gönderim sürerken düğme ve alanlar kapalıdır. Kredi yazmaları ayrıca sunucu
 * tarafında yineleme anahtarıyla korunur: diyalog bir anahtar üretir, aynı
 * gövdeyle ikinci gönderim aynı anahtarı taşır ve ikinci kayıt OLUŞMAZ.
 *
 * SONUCU BİLİNMEYEN HATADA (yanıt yok, zaman aşımı, 5xx) ve 409'da her yazma
 * ilgili veriyi YENİDEN ÇEKER: istek sunucuda işlenmiş olabilir ya da önceki
 * deneme işlemiş olabilir, ekran tahmin etmez (`refetchesAfterFailure`).
 */
export function* organizationsSaga() {
  yield takeLatest(
    fetchOrganizations.request.type,
    apiSaga(fetchOrganizations, organizationsApi.fetchOrganizations),
  );
  yield takeLatest(
    fetchOrganization.request.type,
    apiSaga(fetchOrganization, (arg) => organizationsApi.fetchOrganization(arg.id)),
  );
  // Etkinlik raporları özeti (Story 5): salt okuma, kurumdan kuruma geçişte eskisi iptal.
  yield takeLatest(
    fetchOrganizationReports.request.type,
    apiSaga(fetchOrganizationReports, (arg) =>
      organizationsApi.fetchOrganizationReports(arg.organizationId),
    ),
  );
  // Etkinlik sonrası bölümü (belge ve anket Story 8): salt okuma, kurum geçişinde eskisi iptal.
  yield takeLatest(
    fetchOrganizationPostEvent.request.type,
    apiSaga(fetchOrganizationPostEvent, (arg) =>
      organizationsApi.fetchOrganizationPostEvent(arg.organizationId),
    ),
  );
  // Anketi kapat: başarıda, 409'da ve sonucu bilinmeyen hatada bölüm yeniden çekilir.
  yield takeLatest(
    disableEventSurvey.request.type,
    apiSaga(disableEventSurvey, organizationsApi.disableEventSurvey, {
      onSuccess: function* () {
        // Yanıt kurum taşımaz: bölümün o an gösterdiği kurum tazelenir.
        const organizationId = (yield select(
          (state: RootState) => state.panelOrgPostEvent.organizationId,
        )) as string | null;
        if (organizationId) yield put(fetchOrganizationPostEvent.request({ organizationId }));
      },
      onFailure: function* (error, arg) {
        if (refetchesAfterFailure(error)) {
          yield put(fetchOrganizationPostEvent.request({ organizationId: arg.organizationId }));
        }
      },
    }),
  );
  yield takeLatest(
    provisionOrganization.request.type,
    apiSaga(provisionOrganization, organizationsApi.provisionOrganization, {
      onSuccess: function* () {
        yield* refreshOrganizationList();
        yield* refreshSalesListIfLoaded();
      },
      // Sonuç bilinmiyor: kurum açılmış olabilir, liste gerçeği göstermeli.
      onFailure: function* (error) {
        if (!refetchesAfterFailure(error)) return;
        yield* refreshOrganizationList();
        yield* refreshSalesListIfLoaded();
      },
    }),
  );
  // Salt okuma; alan değişince yeni soru eskisini iptal eder (takeLatest).
  yield takeLatest(
    checkOwnerEmail.request.type,
    apiSaga(checkOwnerEmail, async (arg) => ({
      ownerEmail: arg.ownerEmail,
      emailInPersonalAccount: (await organizationsApi.checkOwnerEmail(arg.ownerEmail))
        .emailInPersonalAccount,
    })),
  );
  yield takeLatest(
    resendOwnerInvitation.request.type,
    apiSaga(
      resendOwnerInvitation,
      async (arg) => ({
        organizationId: arg.organizationId,
        invitation: await organizationsApi.resendOwnerInvitation(arg.organizationId),
      }),
      {
        onSuccess: function* (result) {
          yield put(fetchOrganization.request({ id: result.organizationId }));
          yield* refreshOrganizationList();
        },
        onFailure: function* (error, arg) {
          if (!refetchesAfterFailure(error)) return;
          yield put(fetchOrganization.request({ id: arg.organizationId }));
          yield* refreshOrganizationList();
        },
      },
    ),
  );
  yield takeLatest(
    replaceOwnerInvitation.request.type,
    apiSaga(
      replaceOwnerInvitation,
      async (arg) => ({
        organizationId: arg.organizationId,
        invitation: await organizationsApi.replaceOwnerInvitation(arg),
      }),
      {
        onSuccess: function* (result) {
          yield put(fetchOrganization.request({ id: result.organizationId }));
          yield* refreshOrganizationList();
        },
        // İptal başarılı olup davet düştüyse sahip bloğu gerçeği göstermeli.
        onFailure: function* (error, arg) {
          yield put(fetchOrganization.request({ id: arg.organizationId }));
          if (refetchesAfterFailure(error)) yield* refreshOrganizationList();
        },
      },
    ),
  );

  // Yanıt güncel detaydır (slice yerine yazar); 409 ve bilinmeyen sonuçta detay yeniden çekilir.
  yield takeLatest(
    removeOrganizationCover.request.type,
    apiSaga(removeOrganizationCover, organizationsApi.removeOrganizationCover, {
      onFailure: function* (error, arg) {
        if (refetchesAfterFailure(error)) {
          yield put(fetchOrganization.request({ id: arg.organizationId }));
        }
      },
    }),
  );

  yield takeLatest(
    fetchCreditOverview.request.type,
    apiSaga(fetchCreditOverview, (arg) => creditsApi.fetchCreditOverview(arg.organizationId)),
  );
  yield takeLatest(
    fetchCreditLedger.request.type,
    apiSaga(fetchCreditLedger, creditsApi.fetchCreditLedger),
  );
  yield takeLatest(
    grantCredits.request.type,
    apiSaga(grantCredits, creditsApi.grantCredits, {
      onSuccess: function* (result) {
        yield* refreshCredits(result.organizationId, true);
        yield* refreshGiftSurfaces();
      },
      onFailure: function* (error, arg) {
        if (!refetchesAfterFailure(error)) return;
        yield* refreshCredits(arg.organizationId, true);
        yield* refreshGiftSurfaces();
      },
    }),
  );
  yield takeLatest(
    reverseCredits.request.type,
    apiSaga(reverseCredits, creditsApi.reverseCredits, {
      onSuccess: function* (result) {
        yield* refreshCredits(result.organizationId, true);
      },
      onFailure: function* (error, arg) {
        if (refetchesAfterFailure(error)) yield* refreshCredits(arg.organizationId, true);
      },
    }),
  );
  yield takeLatest(
    createContract.request.type,
    apiSaga(createContract, creditsApi.createContract, {
      onSuccess: function* (result) {
        yield* refreshCredits(result.organizationId, false);
      },
      onFailure: function* (error, arg) {
        if (refetchesAfterFailure(error)) yield* refreshCredits(arg.organizationId, false);
      },
    }),
  );
  yield takeLatest(
    terminateContract.request.type,
    apiSaga(terminateContract, creditsApi.terminateContract, {
      onSuccess: function* (result) {
        yield* refreshCredits(result.organizationId, false);
      },
      onFailure: function* (error, arg) {
        if (refetchesAfterFailure(error)) yield* refreshCredits(arg.organizationId, false);
      },
    }),
  );
}
